import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, addDays, startOfDay } from "date-fns";
import {
  CalendarDays, Clock, Loader2, ArrowUpRight, CheckCircle2, ArrowLeft,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { API } from "../context/SiteContext";

const SESSION_TYPES = [
  { id: "initial", name: "Initial Consultation", duration: 60, blurb: "Meet your therapist · clarify what you need" },
  { id: "individual", name: "Individual Therapy", duration: 50, blurb: "Ongoing one-on-one session" },
  { id: "couples", name: "Couples & Family", duration: 75, blurb: "For two or more · longer format" },
];

const fmtTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
};

export default function BookingCalendar() {
  const today = startOfDay(new Date());
  const [date, setDate] = useState(addDays(today, 1));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [type, setType] = useState("initial");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  const dateStr = useMemo(() => format(date, "yyyy-MM-dd"), [date]);

  useEffect(() => {
    let cancelled = false;
    setLoadingSlots(true);
    setTime("");
    axios
      .get(`${API}/appointments/availability`, { params: { date: dateStr } })
      .then((r) => { if (!cancelled) setSlots(r.data?.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [dateStr]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!time) { toast.error("Pick a time first."); return; }
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/appointments`, {
        date: dateStr,
        time,
        session_type: type,
        ...form,
      });
      setBooked(data);
      toast.success("You're on the calendar.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not book that time.");
    } finally {
      setSubmitting(false);
    }
  };

  const sessionInfo = SESSION_TYPES.find((s) => s.id === type);

  return (
    <section id="book" className="relative py-24 md:py-32 bg-[#FDFBF7]" data-testid="booking-calendar-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-16 items-end">
          <div className="md:col-span-7">
            <p className="overline text-[#9F5C45] mb-6">06 — Book online</p>
            <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Pick a time that <em className="italic text-[#3A5A40]">feels right.</em>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-[#57534E] text-base md:text-lg leading-relaxed">
              Real-time availability. Book your first session in under two minutes — we'll send a calendar invite and a friendly reminder.
            </p>
          </div>
        </div>

        {booked ? (
          <SuccessPanel booked={booked} onAgain={() => { setBooked(null); setForm({ full_name: "", email: "", phone: "", notes: "" }); setTime(""); }} />
        ) : (
          <div className="grid lg:grid-cols-[1fr_320px_1fr] gap-4">
            {/* Session type */}
            <div className="bg-[#F4F1EA] border border-[#E2DCD0] p-6 md:p-8" data-testid="booking-types">
              <div className="overline text-[#9F5C45] mb-4">Step 1 · Session type</div>
              <div className="space-y-3">
                {SESSION_TYPES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setType(s.id)}
                    data-testid={`booking-type-${s.id}`}
                    className={`w-full text-left p-4 transition-all border ${
                      type === s.id
                        ? "border-[#3A5A40] bg-white"
                        : "border-transparent bg-white/60 hover:bg-white"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <span className="font-serif text-lg text-[#1C1917]">{s.name}</span>
                      <span className="text-xs uppercase tracking-[0.18em] text-[#57534E] flex-shrink-0">{s.duration} min</span>
                    </div>
                    <p className="text-sm text-[#57534E]">{s.blurb}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Date picker */}
            <div className="bg-white border border-[#E2DCD0] p-6 md:p-4 flex flex-col items-center" data-testid="booking-calendar">
              <div className="overline text-[#9F5C45] mb-2 self-start px-2">Step 2 · Date</div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                disabled={(d) => d < today || d > addDays(today, 60)}
                className="rounded-none"
              />
            </div>

            {/* Time slots */}
            <div className="bg-[#F4F1EA] border border-[#E2DCD0] p-6 md:p-8 flex flex-col" data-testid="booking-slots">
              <div className="flex items-center justify-between mb-4">
                <div className="overline text-[#9F5C45]">Step 3 · Time</div>
                <div className="text-xs text-[#57534E] flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> {format(date, "EEE, MMM d")}
                </div>
              </div>

              {loadingSlots ? (
                <div className="flex-1 flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-[#3A5A40]" />
                </div>
              ) : slots.length === 0 ? (
                <p className="text-sm text-[#57534E] italic font-serif py-6">
                  No openings on this day — try another date.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 overflow-y-auto pr-1" style={{ maxHeight: 340 }}>
                  {slots.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTime(t)}
                      data-testid={`booking-slot-${t}`}
                      className={`px-3 py-2.5 text-sm transition-colors ${
                        time === t
                          ? "bg-[#3A5A40] text-[#FDFBF7]"
                          : "bg-white text-[#1C1917] hover:bg-[#EAE5D9]"
                      }`}
                    >
                      {fmtTime(t)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Patient form (shows when time picked, not yet booked) */}
        {!booked && time && (
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="mt-6 bg-white border border-[#E2DCD0] p-8 md:p-12 grid md:grid-cols-[1fr_280px] gap-10"
          >
            <div className="space-y-7">
              <div>
                <div className="overline text-[#9F5C45] mb-2">Step 4 · Your details</div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1C1917]">
                  Almost there.
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Full name" required>
                  <input className="lgw-field" data-testid="booking-input-name" value={form.full_name} onChange={upd("full_name")} placeholder="Jane Doe" />
                </Field>
                <Field label="Phone" required>
                  <input className="lgw-field" data-testid="booking-input-phone" value={form.phone} onChange={upd("phone")} placeholder="(203) 000-0000" />
                </Field>
              </div>
              <Field label="Email" required>
                <input type="email" className="lgw-field" data-testid="booking-input-email" value={form.email} onChange={upd("email")} placeholder="jane@example.com" />
              </Field>
              <Field label="Notes (optional)">
                <textarea rows={3} className="lgw-field resize-none" data-testid="booking-input-notes" value={form.notes} onChange={upd("notes")} placeholder="Anything you'd like your therapist to know in advance" />
              </Field>
            </div>

            <div className="space-y-6">
              <Summary
                sessionType={sessionInfo}
                date={date}
                time={time}
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 btn-primary text-sm tracking-wide disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking</> : <>Confirm booking <ArrowUpRight className="w-4 h-4" /></>}
              </button>
              <p className="text-xs text-[#57534E] leading-relaxed">
                We'll email you a confirmation and reminder. No card required at booking.
              </p>
            </div>
          </form>
        )}

        <p className="text-xs text-[#57534E] mt-8 text-center">
          <Clock className="w-3.5 h-3.5 inline-block -mt-0.5 mr-1.5" />
          Hours: Mon–Fri 8am–8pm · Sat 9am–4pm · Closed Sundays · All times Eastern.
        </p>
      </div>
    </section>
  );
}

// ---------- helpers ----------
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="overline text-[#57534E] mb-2 block">
      {label}{required && <span className="text-[#9F5C45] ml-1">*</span>}
    </span>
    {children}
  </label>
);

const Summary = ({ sessionType, date, time }) => (
  <div className="bg-[#F4F1EA] p-6 border border-[#E2DCD0]" data-testid="booking-summary">
    <div className="overline text-[#9F5C45] mb-3">Your appointment</div>
    <p className="font-serif text-xl text-[#1C1917] mb-3 leading-tight">
      {sessionType?.name}
    </p>
    <div className="space-y-2 text-sm text-[#57534E]">
      <p className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> {format(date, "EEEE, MMMM d, yyyy")}</p>
      <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {fmtTime(time)} · {sessionType?.duration} min</p>
    </div>
  </div>
);

const SuccessPanel = ({ booked, onAgain }) => (
  <div className="bg-[#3A5A40] text-[#FDFBF7] p-12 md:p-16 text-center" data-testid="booking-success">
    <CheckCircle2 className="w-12 h-12 mx-auto mb-6 text-[#EAE5D9]" strokeWidth={1.25} />
    <p className="overline text-[#EAE5D9] mb-4">Confirmed</p>
    <h3 className="font-serif font-light text-4xl md:text-5xl mb-4 tracking-tight">
      We have you on the calendar.
    </h3>
    <p className="text-[#EAE5D9] max-w-xl mx-auto leading-relaxed mb-2">
      A confirmation has been emailed to <strong className="font-medium">{booked.email}</strong>.
    </p>
    <p className="font-serif text-2xl text-[#FDFBF7] my-6">
      {booked.date} · {fmtTime(booked.time)} <span className="text-base opacity-80">({booked.duration_minutes} min)</span>
    </p>
    <button
      onClick={onAgain}
      data-testid="booking-again"
      className="inline-flex items-center gap-2 px-6 py-3 border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#3A5A40] transition-colors text-sm tracking-wide"
    >
      <ArrowLeft className="w-4 h-4" /> Book another session
    </button>
  </div>
);
